import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AssetUIModel, CheckSessionService, FileObject, NotificationService } from '@thehubz/client/core';
import {
  Asset,
  CreateMultiPartUploadDto,
  ListMultiPartDto,
  MultiPartObjDto,
  ObjectPropertiesDto,
  UploadedPartsDto,
  UploadsApiService,
} from '@thehubz/client/data-access-api';
import { AnyType, createAssetS3Key, createVersionString } from '@thehubz/shared/model';
import { BehaviorSubject, Subject, lastValueFrom } from 'rxjs';

export interface UploadFileObj {
  file: File;
  uploadId: string;
  parts: number;
  preSignedUrls: Record<number, string>;
  key: string;
  name: string;
  uploadedParts?: UploadedPartsDto;
}

interface SingleFilePromise {
  ETag: string;
  PartNumber: number;
}

/* Please refer to README.md for process info */

@Injectable({
  providedIn: 'root',
})
export class FileUploadService {
  private FILE_CHUNK_SIZE = 40000000;
  private singleFilePromises = new BehaviorSubject(<SingleFilePromise[]>[]);

  uploadedFileSubject = new Subject();
  fileUploadStartSubject = new Subject<UploadFileObj>();
  progress = new BehaviorSubject(0);
  totalParts = 0;
  private progressCount = 0;

  constructor(
    private uploadsApiService: UploadsApiService,
    private http: HttpClient,
    private notificationsService: NotificationService,
    private checkSessionService: CheckSessionService,
  ) {}

  /**
   * Create a upload key (folder in S3 bucket) for the asset including the version.
   *
   * @param asset
   * @returns A file key
   */
  static createAssetFileKey(asset: AssetUIModel, overrideName?: string, uploadFlag?: boolean, version?: number) {
    // the asset is created before we upload the file, so latest version is the one we try to upload
    if (!asset.mainFile) {
      throw new Error(`the asset ${asset._id} does not contain any file`);
    }
    // hold the asset file path of the s3 bucket
    let assetFileKey = '';
    const assetVersion = uploadFlag ? asset.mainFile.version + 1 : asset.mainFile.version;
    const assetAspectRatio = asset.mainFile.defaultAspectRatio;
    const assetFileFormat = asset.mainFile.defaultFileFormat;
    const versionString = createVersionString(version || assetVersion);
    // check is for the banner's fallback image that get generated inside 'gen-preview' path
    // else: is for the rest of the asset
    if (asset.assetType === Asset.AssetTypeEnum.HtmlBanner && overrideName) {
      assetFileKey = createAssetS3Key(asset._id, versionString, assetAspectRatio, `gen-preview/${overrideName}`);
    } else {
      assetFileKey = createAssetS3Key(asset._id, versionString, assetAspectRatio, overrideName || assetFileFormat + '.' + asset.mainFile.ext);
    }
    return assetFileKey;
  }

  /**
   * Create a upload key (folder in S3 bucket) for the asset including the version.
   *
   * @param asset
   * @returns A file key
   */
  static createAssetFileKeyForAspectRation(asset: AssetUIModel, formValues: AnyType, fileObj: FileObject) {
    // the asset is created before we upload the file, so latest version is the one we try to upload
    if (!formValues.assetId || !fileObj.file) {
      throw new Error(`the asset ${formValues.assetId} does not contain any file`);
    }

    formValues.mainFile = {
      name: fileObj.name,
      mimetype: fileObj.file.type,
      size: fileObj.file.size,
      ext: fileObj.file.name.split('.').pop() || '',
    };
    const assetAspectRatio = formValues.aspect_ratio;
    const assetFileFormat = asset.mainFile?.defaultFileFormat;
    const versionString = createVersionString(asset.currentVersion?.version);
    return `assets/${formValues.assetId}/${versionString}/${assetAspectRatio}/${assetFileFormat + '.' + asset.mainFile?.ext}`;
  }

  /**
   * Create a upload key (folder in S3 bucket) for the asset including the version.
   *
   * @param asset
   * @returns A file key
   */
  static getKeyForAspectRatio(asset: AssetUIModel, aspectRation: string, assetFileFormat: string) {
    const assetAspectRatio = aspectRation;
    const versionString = createVersionString(asset.mainFile.version);
    return `assets/${asset._id}/${versionString}/${assetAspectRatio}/${assetFileFormat}`;
  }

  /**
   * Public method which we can call in components to initiate the upload for given files.
   * Here we are first getting the file parts {key, parts}[].
   * Passing this parts array to createMultiPartUploadDto create a multipart upload.
   * After receiving the response from createMultiPartUpload we wil start uploading the parts using the presignedurl
   * After uploading a part we will save the {ETag, PartNumber} in an singleFilePromises array which
   * we can later send to backend complete the upload of that file.
   *
   * @param files
   */
  async startMultiPartUpload(files: { key: string; file: File }[]) {
    const fileDataObj = this.getFileParts(files);
    const fileObjs = await this.createMultiPartUpload({ objectProps: fileDataObj }, files);
    for await (const fileObj of fileObjs) {
      this.checkSessionService.trackActivity();
      await this.uploadParts(fileObj);
      const etagsArr = this.singleFilePromises.getValue();
      const promiseObj = {
        etagsArr,
        key: fileObj.key,
        name: fileObj.name,
        uploadId: fileObj.uploadId,
      };
      await this.completeMultiPartUpload(promiseObj);
    }
  }

  /**
   * To get the incomplete uploads list of all files.
   *
   * @param fileobj
   */
  async abortMultiPartUpload(fileobj: MultiPartObjDto) {
    const res = await lastValueFrom(this.uploadsApiService.apiUploadsControllerAbortMultiPartUpload(fileobj));
    if (res.statusCode === 200) {
      this.notificationsService.addNotification({
        type: 'success',
        message: `${res.Key} uploading aborted`,
      });
    }
  }

  /**
   * To get the incomplete uploads list of all files.
   *
   * @param name
   * @param key
   * @returns
   */
  async checkIfFileExists(name: string, key?: string) {
    const res = await lastValueFrom(this.uploadsApiService.apiUploadsControllerListIncompleteUploads(name, key));
    return res;
  }

  /**
   * Delete file from asset.
   *
   * @param id
   * @returns
   */
  async deleteFile(id: string) {
    return lastValueFrom(this.uploadsApiService.apiUploadsControllerDelete(id));
  }

  /**
   * Delete user profile image.
   *
   * @param id user id
   * @returns
   */
  async deleteUserFile(id: string) {
    return lastValueFrom(this.uploadsApiService.apiUploadsControllerDeleteUserObject(id));
  }

  /**
   * Getting the file parts length and name of file Also using the calculateTotalParts
   * to calculate the total parts of all files for multiple file uploads
   *
   * @param fileObjs
   * @returns
   */
  private getFileParts(fileObjs: { key: string; file: File }[]): ObjectPropertiesDto[] {
    const fileDataArr = [];
    for (const fileObj of fileObjs) {
      const key = fileObj.key;
      const name = fileObj.file.name;
      const parts = Math.ceil(fileObj.file.size / this.FILE_CHUNK_SIZE);
      fileDataArr.push({ key, parts, name });
    }
    this.calculateTotalParts(fileDataArr);
    return fileDataArr;
  }

  /**
   * Creating the multipart upload for given files Response will contain the uploadId,
   * key and presignedUrls for parts.
   * We will also be storing the uploadId and key in db until uploads completes or aborts.
   * For incomplete uploads, response will also contain an array of uploadedParts {ETag, PartNumber}[]
   * which we can use and only upload remaining parts.
   *
   * @param object
   * @param files
   * @returns
   */
  private async createMultiPartUpload(object: CreateMultiPartUploadDto, files: { key: string; file: File }[]) {
    let filesObjs = await lastValueFrom(this.uploadsApiService.apiUploadsControllerCreateMultiPartUpload(object));
    filesObjs = filesObjs.map((fileobj: MultiPartObjDto) => {
      const file = files.find((r: { key: string; file: File }) => this.normalizeFileName(fileobj.name) === this.normalizeFileName(r.file.name));
      return {
        ...fileobj,
        file: file?.file,
      };
    });
    return filesObjs;
  }

  normalizeFileName = (name: string) => name.replace(/\s+/g, ' ').trim();

  /**
   * We are uploading the file part to presignedurls here.
   * Dividing the file in chunks of FILE_CHUNK_SIZE defined above and uploading to presignedurls
   * For incomplete uploads, we will save the uploadedParts.parts to singleFilePromises array which
   * should have all the {ETag, PartNumber} of all parts of an uploaded file.
   * We will keep updating the singleFilePromises array with remaining parts.
   * The part index will differ for incomplete and new uploads, as new uploads will start from first
   * part whereas incomplete upload will start from first part of remaining parts.
   * The singnedUrls object will by default have these indexes as per condition.
   * We are also updating the progress count here.
   *
   * @param fileObj
   */
  private async uploadParts(fileObj: UploadFileObj) {
    this.progressCount = 0;
    this.fileUploadStartSubject.next(fileObj);
    if (fileObj.uploadedParts) {
      this.progressCount = fileObj.uploadedParts.nextPartNumberMarker;
      this.calculatePercentage(this.progressCount);
      this.singleFilePromises.next(fileObj.uploadedParts.parts);
    }
    const keys = Object.keys(fileObj.preSignedUrls);
    for await (const indexStr of keys) {
      this.checkSessionService.trackActivity();
      const index = parseInt(indexStr);
      const start = index * this.FILE_CHUNK_SIZE;
      const end = (index + 1) * this.FILE_CHUNK_SIZE;
      const blob = index < keys.length ? fileObj.file.slice(start, end) : fileObj.file.slice(start);
      const res = await lastValueFrom(
        this.http.put(fileObj.preSignedUrls[index], blob, {
          observe: 'response',
          headers: {
            'Content-Type': fileObj.file.type,
          },
        }),
      );
      const resObj = { ETag: res.headers.get('ETag') || '', PartNumber: index + 1 };
      const objArr = this.singleFilePromises.getValue();
      this.singleFilePromises.next(objArr.concat(resObj));
      this.progressCount = this.progressCount + 1;
      this.calculatePercentage(this.progressCount);
    }
  }

  /* We are calculating the total parts of all files selected to upload */
  private calculateTotalParts(fileObjs: ObjectPropertiesDto[]) {
    this.totalParts = fileObjs.map((fileobj: ObjectPropertiesDto) => Number(fileobj.parts)).reduce((prev: number, curr: number) => prev + curr, 0);
  }

  /* Calculating the percentage of uploaded parts out of total parts */
  private calculatePercentage(completed: number) {
    const percentage = Math.round((completed * 100) / this.totalParts);
    this.progress.next(percentage);
  }

  /* Calling this whenever all file parts are uploaded passing the uploadId, key,
  singleFilePromises of etags array   */
  private async completeMultiPartUpload(fileobj: MultiPartObjDto) {
    const res = await lastValueFrom(this.uploadsApiService.apiUploadsControllerCompleteMultiPartUpload(fileobj));
    if (res.Location) {
      this.singleFilePromises.next([]);
      this.uploadedFileSubject.next({ res, key: fileobj.key });
      this.notificationsService.addNotification({
        type: 'success',
        message: `${fileobj.name} ` + $localize`successfully uploaded`,
      });
    }
  }

  /* To get the incomplete uploads list of all files */
  private async getIncompleteUploads(files: File[]) {
    const names = this.getFileNames(files);
    const fileObj: any = { listPartsObj: names };
    const res = await this.getMultiPartList(fileObj);
    return res;
  }

  /* To get the file names of files selected to upload */
  private getFileNames(files: File[]): { name: string }[] {
    const fileDataArr = [];
    for (const file of files) {
      const name = file.name;
      fileDataArr.push({ name });
    }
    return fileDataArr;
  }

  /* To get the incomplete uploads list {ETag, PartNumber}[] of a file by
  name and uploadId which would be saved on our db */
  private async getMultiPartList(fileobj: ListMultiPartDto) {
    const res = await lastValueFrom(this.uploadsApiService.apiUploadsControllerListMultiPartUpload(fileobj));
    return res;
  }

  /* To read a file */
  readFile(blob: Blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const base64data = reader.result;
        resolve(base64data);
      };
      reader.onerror = reject;
    });
  }
}
